import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Package } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency, formatDate, formatNumber } from "@/utils/format";
import { useModules } from "@/context/ModulesContext";
import {
  fetchPurchaseRequestItemsAll,
  fetchOrderItemsHistorico,
  computeInsumoPrecos,
  type PurchaseRequestItemRow,
  type InsumoPrecoRef,
  type OrderItem,
  type SolicitacaoCard,
  buildSolicitacaoObraLine,
  effectiveSolicitacaoCostCenterId,
} from "@/services/suprimentos";
import { cn } from "@/utils/cn";

function authLabel(a: boolean | null): string {
  if (a === true) return "Sim";
  if (a === false) return "Não";
  return "—";
}

function precoTitle(ref: InsumoPrecoRef | null | undefined): string | undefined {
  if (!ref) return undefined;
  const parts: string[] = [];
  if (ref.orderDate) parts.push(`Pedido em ${formatDate(ref.orderDate)}`);
  if (ref.orderId) parts.push(`PC ${ref.orderId}`);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

function PrecoCell({
  loading,
  preco,
}: {
  loading: boolean;
  preco: InsumoPrecoRef | null | undefined;
}) {
  if (preco) {
    return (
      <span className="tabular-nums text-slate-700" title={precoTitle(preco)}>
        {formatCurrency(preco.price)}
      </span>
    );
  }
  if (loading) {
    return <span className="text-slate-400">…</span>;
  }
  return <span className="text-slate-400">—</span>;
}

export function SolicitacaoItensModal({
  solicitacao,
  onClose,
  itemQueryPeriod,
}: {
  solicitacao: SolicitacaoCard;
  onClose: () => void;
  /** Período do filtro do Kanban — necessário para o Sienge all/items retornar linhas. */
  itemQueryPeriod?: { startDate: string; endDate: string };
}) {
  const { ccNameMap, suprimentos } = useModules();
  const { orderItems } = suprimentos;
  const [items, setItems] = useState<PurchaseRequestItemRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [histItems, setHistItems] = useState<OrderItem[]>([]);
  const [histLoading, setHistLoading] = useState(true);

  /* Histórico de pedidos dos últimos 6 meses (todas as obras) — independente do
     filtro da aba; resultados parciais vão preenchendo os preços conforme chegam. */
  useEffect(() => {
    let cancelled = false;
    setHistLoading(true);
    fetchOrderItemsHistorico((partial) => {
      if (!cancelled) setHistItems(partial);
    })
      .then((all) => {
        if (!cancelled) setHistItems(all);
      })
      .catch(() => {
        /* mantém o que já chegou; células sem match mostram "—" */
      })
      .finally(() => {
        if (!cancelled) setHistLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPurchaseRequestItemsAll(solicitacao.purchaseRequestId, itemQueryPeriod)
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setItems([]);
          setError(e instanceof Error ? e.message : "Não foi possível carregar os itens.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [solicitacao.purchaseRequestId, itemQueryPeriod?.startDate, itemQueryPeriod?.endDate]);

  /* Preços: histórico de 6 meses + itens já carregados na aba (resultado imediato).
     Match estrito: SC.productId ↔ PC.resourceId. */
  const precos = useMemo(() => {
    if (!items?.length) return null;
    const ids = [...new Set(items.map((it) => it.productId).filter(Boolean))];
    return computeInsumoPrecos([...histItems, ...orderItems], ids);
  }, [items, orderItems, histItems]);
  const precosLoading = histLoading;

  const obra = buildSolicitacaoObraLine(solicitacao);
  const effCc = effectiveSolicitacaoCostCenterId(solicitacao);
  const fromMap = effCc ? (ccNameMap.get(effCc) ?? ccNameMap.get(String(Number(effCc))) ?? "").trim() : "";
  const ccDisplay =
    (solicitacao.costCenterName ?? "").trim() ||
    fromMap ||
    (effCc ? `Identificador ${effCc}` : "") ||
    "—";

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-50 flex-shrink-0 border border-violet-100">
            <ClipboardList className="w-5 h-5 text-violet-600" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-bold text-slate-800 leading-tight">
              SC {solicitacao.purchaseRequestId}
            </p>
            <p className="text-xs text-slate-500 truncate">{obra}</p>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Centro de custo</p>
            <p className="text-xs font-semibold text-slate-800 mt-0.5 line-clamp-2">{ccDisplay}</p>
          </div>
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Data</p>
            <p className="text-xs font-semibold text-slate-800 mt-0.5">
              {solicitacao.requestDate ? formatDate(solicitacao.requestDate) : "—"}
            </p>
          </div>
          <div className="bg-violet-50 rounded-xl px-3 py-2.5 border border-violet-100">
            <p className="text-[10px] text-violet-700 uppercase tracking-wide font-semibold">Itens (card)</p>
            <p className="text-xs font-bold text-violet-900 mt-0.5 tabular-nums">
              {formatNumber(solicitacao.itemCount)}
            </p>
          </div>
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Maior alçada</p>
            <p className="text-xs font-semibold text-slate-800 mt-0.5 tabular-nums">
              {formatNumber(solicitacao.maxCompetenceLevel)}
            </p>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-slate-400">
            <Loader size="md" label="Carregando itens da solicitação…" />
          </div>
        ) : !items?.length ? (
          solicitacao.itemsPreview && solicitacao.itemsPreview.length > 0 ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2 text-xs text-amber-900">
              <p className="font-semibold mb-2">Prévia dos itens (detalhe completo indisponível nesta consulta)</p>
              <ul className="space-y-1 list-disc list-inside text-slate-700">
                {solicitacao.itemsPreview.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
              <Package className="w-8 h-8 opacity-40" />
              <p className="text-sm">Nenhum item retornado para esta SC.</p>
            </div>
          )
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-[1]">
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-12">
                    #
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide min-w-[180px]">
                    Descrição
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    Qtd.
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-14">
                    Un.
                  </th>
                  <th className="text-center px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-16">
                    Alçada
                  </th>
                  <th className="text-center px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-20">
                    Autoriz.
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    Qtd. aberta
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    Último preço
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    Menor preço (6 meses)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => {
                  const p = it.productId ? precos?.get(it.productId) : undefined;
                  return (
                    <tr key={`${it.itemNumber}-${idx}`} className="hover:bg-slate-50/80">
                      <td className="px-3 py-2 text-slate-600 tabular-nums font-medium">{it.itemNumber || "—"}</td>
                      <td className="px-3 py-2 text-slate-800">{it.description || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatNumber(it.quantity, 3)}</td>
                      <td className="px-3 py-2 text-slate-600">{it.unit || "—"}</td>
                      <td className="px-3 py-2 text-center tabular-nums text-slate-600">
                        {it.competenceLevel != null ? formatNumber(it.competenceLevel) : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={cn(
                            "inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded",
                            it.authorized === true && "bg-emerald-50 text-emerald-800",
                            it.authorized === false && "bg-amber-50 text-amber-800",
                            it.authorized === null && "bg-slate-100 text-slate-500"
                          )}
                        >
                          {authLabel(it.authorized)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                        {it.openQuantity != null ? formatNumber(it.openQuantity, 3) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <PrecoCell loading={precosLoading} preco={p?.ultimoPreco} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <PrecoCell loading={precosLoading} preco={p?.menorPreco} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
